import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal } from '../common';
import {
  Camera,
  Calendar,
  User,
  Clock,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Search,
  Filter,
  X,
  ZoomIn,
  Eye,
  ExternalLink,
  ChevronRight,
  ClipboardList,
  DownloadCloud,
} from 'lucide-react';
import { db, getAllInspections, getAllMachines, getSetting } from '../../db';
import type { InspectionRecord, Machine } from '../../types';
import {
  fetchInspectionHistory,
  type CloudInspectionRecord,
} from '../../services/sync';

export const InspectionHistory: React.FC = () => {
  // Inspections list state
  const [localInspections, setLocalInspections] = useState<InspectionRecord[]>([]);
  const [cloudInspections, setCloudInspections] = useState<CloudInspectionRecord[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchingCloud, setIsFetchingCloud] = useState<boolean>(false);
  const [cloudFeedback, setCloudFeedback] = useState<string>('');
  const [cloudError, setCloudError] = useState<string>('');

  // Filter & Search states
  const [selectedMachineFilter, setSelectedMachineFilter] = useState<string>('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<'ALL' | 'NG' | 'OK'>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Detail Modal state
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);

  // Lightbox Photo Modal state
  const [lightboxPhoto, setLightboxPhoto] = useState<{ url: string; title: string } | null>(null);

  // Load initial local inspections & machines
  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [allIns, allMach] = await Promise.all([
        getAllInspections(),
        getAllMachines(),
      ]);
      setLocalInspections(allIns);
      setMachines(allMach);
    } catch (err) {
      console.error('[InspectionHistory] Error loading local data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // Fetch Cloud History from Google Sheets Web App
  const handleFetchCloudHistory = async () => {
    setCloudError('');
    setCloudFeedback('');

    try {
      const storedUrl = (await getSetting<string>('gas_endpoint_url')) || '';
      const storedConfig = await getSetting<{ webAppUrl?: string }>('gas_config');
      const webAppUrl = storedConfig?.webAppUrl || storedUrl;

      if (!webAppUrl) {
        setCloudError('ยังไม่ได้กำหนด Google Apps Script Web App URL กรุณาไปที่แท็บ Settings เพื่อวาง URL');
        return;
      }

      setIsFetchingCloud(true);
      const result = await fetchInspectionHistory(webAppUrl, 100);

      if (result.success && result.inspections) {
        setCloudInspections(result.inspections);
        setCloudFeedback(`ดึงประวัติจาก Google Sheets สำเร็จ ${result.inspections.length} รายการ`);
        setTimeout(() => setCloudFeedback(''), 4000);
      } else {
        setCloudError(result.message || 'ไม่สามารถดึงข้อมูลจาก Google Sheets ได้');
      }
    } catch (err: any) {
      setCloudError(`เกิดข้อผิดพลาดในการดึงข้อมูล: ${err.message || 'Network error'}`);
    } finally {
      setIsFetchingCloud(false);
    }
  };

  // Combine and deduplicate local and cloud inspections
  const mergedInspections = useMemo(() => {
    const map = new Map<string, any>();

    // 1. Add cloud inspections first
    for (const c of cloudInspections) {
      const id = c.id || c.submissionId;
      map.set(id, {
        ...c,
        isCloud: true,
        items: c.items || [],
      });
    }

    // 2. Add or overlay local inspections (they contain raw signatures and local details)
    for (const l of localInspections) {
      const id = l.id;
      const existing = map.get(id);

      const localResults = l.results || [];
      // Count local photos defensively across photoUrl, photo, and photoBase64
      const localPhotoCount = localResults.filter((item: any) => {
        const p = item.photoUrl || item.photo || item.photoBase64;
        return typeof p === 'string' && (p.startsWith('data:image') || p.startsWith('http://') || p.startsWith('https://'));
      }).length;

      map.set(id, {
        id: l.id,
        submissionId: l.id,
        inspectionDate: l.inspectionDate,
        machineId: l.machineId,
        machineName: l.machineName,
        inspectorName: l.inspectorName,
        frequency: l.frequency,
        runningHours: l.operatingHours,
        overallResult: l.overallStatus,
        supervisorNotes: l.supervisorNotes,
        signatureDataUrl: l.signatureDataUrl,
        isLocal: true,
        isCloud: !!existing,
        photoCount: Math.max(localPhotoCount, existing?.photoCount || 0),
        items: localResults.map((it: any) => {
          // If local doesn't have photoUrl but cloud has a Drive URL, merge it
          const cloudItem = existing?.items?.find((ci: any) => String(ci.itemNo) === String(it.itemNo));
          const rawUrl = it.photoUrl || it.photo || it.photoBase64 || cloudItem?.photoUrl;
          const isRealUrl = typeof rawUrl === 'string' && (rawUrl.startsWith('data:image') || rawUrl.startsWith('http://') || rawUrl.startsWith('https://'));
          const pUrl = isRealUrl ? rawUrl : undefined;
          return {
            itemNo: it.itemNo,
            taskNameTh: it.taskNameTh || it.taskName || `Item #${it.itemNo}`,
            criteriaTh: it.criteriaTh || it.criteria || '',
            methodTh: it.methodTh || '',
            measuredValue: it.numericValue != null ? String(it.numericValue) : (it.measuredValue != null ? String(it.measuredValue) : it.selectedOption),
            status: it.status,
            minValue: it.minValue,
            maxValue: it.maxValue,
            notes: it.notes,
            photoUrl: pUrl,
            hasPhoto: !!isRealUrl || it.hasPhoto === true || cloudItem?.hasPhoto === true,
          };
        }),
      });
    }

    // Convert map to sorted array (newest first)
    return Array.from(map.values()).sort((a, b) => {
      const dateA = new Date(a.inspectionDate).getTime() || 0;
      const dateB = new Date(b.inspectionDate).getTime() || 0;
      return dateB - dateA;
    });
  }, [localInspections, cloudInspections]);

  // Apply filters and search
  const filteredInspections = useMemo(() => {
    return mergedInspections.filter((rec) => {
      // Machine Filter
      if (selectedMachineFilter !== 'ALL' && rec.machineId !== selectedMachineFilter) {
        return false;
      }

      // Status Filter
      if (selectedStatusFilter === 'NG') {
        const isNG = rec.overallResult === 'FAIL' || rec.overallResult === 'NG' ||
          rec.items.some((it: any) => it.status === 'NG');
        if (!isNG) return false;
      } else if (selectedStatusFilter === 'OK') {
        const isOK = rec.overallResult === 'PASS' || rec.overallResult === 'OK';
        if (!isOK) return false;
      }

      // Search text
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchInspector = (rec.inspectorName || '').toLowerCase().includes(q);
        const matchMachine = (rec.machineName || '').toLowerCase().includes(q) || (rec.machineId || '').toLowerCase().includes(q);
        const matchNotes = (rec.supervisorNotes || '').toLowerCase().includes(q);
        if (!matchInspector && !matchMachine && !matchNotes) return false;
      }

      return true;
    });
  }, [mergedInspections, selectedMachineFilter, selectedStatusFilter, searchTerm]);

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Top Header & Cloud Fetch Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            ประวัติการตรวจเช็กและหลักฐานรูปถ่าย (Inspection History & Photos)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            ตรวจสอบรายงานผลการตรวจเช็กย้อนหลัง รายการข้อบกพร่อง รูปถ่ายหน้างาน และลายเซ็นผู้ตรวจสอบ
          </p>
        </div>

        <Button
          type="button"
          variant="secondary"
          onClick={handleFetchCloudHistory}
          isLoading={isFetchingCloud}
          leftIcon={<DownloadCloud className="w-4 h-4 text-blue-600" />}
          className="self-start sm:self-auto min-h-[44px]"
        >
          ดึงประวัติจาก Google Sheets (Fetch Cloud)
        </Button>
      </div>

      {/* Cloud Feedback Banners */}
      {cloudFeedback && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs rounded-xl flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{cloudFeedback}</span>
        </div>
      )}
      {cloudError && (
        <div className="p-3.5 bg-rose-50 border border-rose-300 text-rose-800 text-xs rounded-xl flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{cloudError}</span>
        </div>
      )}

      {/* Filter & Search Bar */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Machine Filter */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              เครื่องจักร (Machine)
            </label>
            <select
              value={selectedMachineFilter}
              onChange={(e) => setSelectedMachineFilter(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 min-h-[44px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="ALL">เครื่องจักรทั้งหมด (All Machines)</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.id})
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              สถานะผลตรวจ (Status)
            </label>
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value as any)}
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 min-h-[44px] focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="ALL">ผลตรวจทั้งหมด (All Statuses)</option>
              <option value="NG">เฉพาะพบข้อบกพร่อง (NG Defect Only)</option>
              <option value="OK">ปกติทั้งหมด (OK Only)</option>
            </select>
          </div>

          {/* Search Input */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              ค้นหา (Search)
            </label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ชื่อช่าง, เครื่องจักร หรือหมายเหตุ..."
                className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-900 min-h-[44px] focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-slate-400"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Inspections List */}
      {isLoading ? (
        <div className="p-12 text-center text-slate-400 flex items-center justify-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
          <span>กำลังโหลดประวัติการตรวจเช็ก...</span>
        </div>
      ) : filteredInspections.length === 0 ? (
        <Card>
          <div className="p-12 text-center text-slate-400 space-y-2">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">ไม่พบประวัติการตรวจเช็กตามเงื่อนไขที่เลือก</p>
            <p className="text-xs text-slate-400">
              เมื่อมีการบันทึกผลการตรวจเช็ก หรือกดปุ่ม "ดึงประวัติจาก Google Sheets" ข้อมูลจะปรากฏที่นี่
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500 px-1">
            <span>พบทั้งหมด <strong>{filteredInspections.length}</strong> รายการ</span>
            <span>เรียงจากล่าสุดไปเก่าสุด</span>
          </div>

          {filteredInspections.map((rec) => {
            const isNG = rec.overallResult === 'FAIL' || rec.overallResult === 'NG' ||
              rec.items.some((it: any) => it.status === 'NG');
            const realPhotos = (rec.items || []).filter(
              (it: any) => !!it.photoUrl && (it.photoUrl.startsWith('data:image') || it.photoUrl.startsWith('http://') || it.photoUrl.startsWith('https://'))
            );
            const hasPhotos = realPhotos.length > 0;

            return (
              <div
                key={rec.id || rec.submissionId}
                onClick={() => setSelectedRecord(rec)}
                className="p-4 sm:p-5 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-md transition-all cursor-pointer space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${
                        isNG
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}
                    >
                      {isNG ? 'พบข้อบกพร่อง (NG)' : 'ปกติ (OK)'}
                    </span>

                    <span className="font-bold text-slate-900 text-base">
                      {rec.machineName || rec.machineId}
                    </span>

                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-blue-700 font-semibold border border-slate-200">
                      {rec.machineId}
                    </span>

                    <span className="text-xs font-semibold text-slate-500 uppercase px-2 py-0.5 rounded bg-slate-100">
                      {rec.frequency}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>{new Date(rec.inspectionDate).toLocaleString('th-TH')}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span>ผู้ตรวจ: <strong className="text-slate-800">{rec.inspectorName || 'ไม่ระบุ'}</strong></span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    <span>ชั่วโมงสะสม: <strong className="text-slate-800">{rec.runningHours ? `${Number(rec.runningHours).toLocaleString()} ชม.` : '-'}</strong></span>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-2.5">
                    {hasPhotos && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxPhoto({
                            url: realPhotos[0].photoUrl,
                            title: `ภาพหลักฐาน: ข้อ #${realPhotos[0].itemNo} ${realPhotos[0].taskNameTh}`,
                          });
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white text-xs font-bold shadow-xs hover:shadow transition-all cursor-pointer"
                        title="คลิกเพื่อเปิดดูรูปถ่ายทันที"
                      >
                        <Camera className="w-3.5 h-3.5" />
                        <span>ดูรูปถ่าย ({realPhotos.length} รูป)</span>
                      </button>
                    )}

                    <span className="text-blue-600 font-semibold flex items-center gap-0.5 text-xs hover:underline">
                      ดูรายละเอียด <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {selectedRecord && (() => {
        const recordPhotos = (selectedRecord.items || []).filter(
          (it: any) => !!it.photoUrl && (it.photoUrl.startsWith('data:image') || it.photoUrl.startsWith('http://') || it.photoUrl.startsWith('https://'))
        );

        return (
          <Modal
            isOpen={!!selectedRecord}
            onClose={() => setSelectedRecord(null)}
            title={
              <div className="flex items-center justify-between gap-3 w-full pr-4">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  <span className="font-bold">
                    บันทึกการตรวจเช็ก: {selectedRecord.machineName} ({selectedRecord.machineId})
                  </span>
                </div>
                {recordPhotos.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs font-bold shrink-0">
                    <Camera className="w-3.5 h-3.5" />
                    {recordPhotos.length} รูปถ่าย
                  </span>
                )}
              </div>
            }
            maxWidth="xl"
            footer={
              <Button variant="secondary" onClick={() => setSelectedRecord(null)}>
                ปิดหน้าต่าง
              </Button>
            }
          >
            <div className="space-y-5">
              {/* Meta info header */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">วันที่ตรวจ</span>
                  <strong className="text-slate-800">
                    {new Date(selectedRecord.inspectionDate).toLocaleString('th-TH')}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">ผู้ตรวจสอบ</span>
                  <strong className="text-slate-800">{selectedRecord.inspectorName || '-'}</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">รอบความถี่</span>
                  <strong className="text-slate-800 uppercase">{selectedRecord.frequency || '-'}</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">ชั่วโมงสะสม</span>
                  <strong className="text-slate-800">
                    {selectedRecord.runningHours ? `${Number(selectedRecord.runningHours).toLocaleString()} ชม.` : '-'}
                  </strong>
                </div>
              </div>

              {/* Attached Photos Gallery Section - Prominently displayed at top */}
              {recordPhotos.length > 0 && (
                <div className="p-4 bg-purple-50/90 border-2 border-purple-300 rounded-xl space-y-3 shadow-xs">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-600 text-white rounded-lg shadow-2xs">
                        <Camera className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-purple-950 flex items-center gap-2">
                          <span>รูปถ่ายหลักฐานที่แนบมา</span>
                          <span className="px-2 py-0.5 rounded-full bg-purple-200 text-purple-900 text-xs">
                            {recordPhotos.length} รูป
                          </span>
                        </h4>
                        <p className="text-[11px] text-purple-700">
                          กดที่รูปภาพหรือปุ่ม "ดูรูปขนาดใหญ่" เพื่อเปิดดูภาพเต็มหน้าจอ
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {recordPhotos.map((pItem: any, pIdx: number) => (
                      <div
                        key={pIdx}
                        onClick={() =>
                          setLightboxPhoto({
                            url: pItem.photoUrl,
                            title: `ภาพหลักฐาน: ข้อ #${pItem.itemNo} ${pItem.taskNameTh}`,
                          })
                        }
                        className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-purple-200 hover:border-purple-500 hover:shadow-md transition-all cursor-pointer group"
                      >
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100 border border-slate-300 shrink-0">
                          <img
                            src={pItem.photoUrl}
                            alt="Defect evidence thumbnail"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                            <ZoomIn className="w-6 h-6" />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-bold">
                              ข้อ #{pItem.itemNo}
                            </span>
                            <span className="text-xs font-bold text-slate-800 truncate block">
                              {pItem.taskNameTh}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                          >
                            <ZoomIn className="w-3.5 h-3.5" /> ดูรูปขนาดใหญ่
                          </button>

                          {pItem.photoUrl.startsWith('http') && (
                            <div>
                              <a
                                href={pItem.photoUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 underline font-semibold"
                              >
                                <ExternalLink className="w-3 h-3" /> เปิดดูใน Google Drive
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supervisor Notes */}
              {selectedRecord.supervisorNotes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                  <strong className="font-bold block mb-1">ข้อคิดเห็น / คำสั่งการแก้ไขของหัวหน้างาน:</strong>
                  <p>{selectedRecord.supervisorNotes}</p>
                </div>
              )}

              {/* Items Checklist Breakdown */}
              <div className="space-y-3">
                <h4 className="font-bold text-sm text-slate-900">
                  รายการตรวจเช็ก ({selectedRecord.items?.length || 0} ข้อ)
                </h4>

              <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
                {selectedRecord.items && selectedRecord.items.map((item: any, idx: number) => {
                  const isItemNG = item.status === 'NG';
                  return (
                    <div
                      key={idx}
                      className={`p-3.5 rounded-xl border transition-all ${
                        isItemNG
                          ? 'bg-rose-50/60 border-rose-200'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-500">
                              #{item.itemNo || idx + 1}
                            </span>
                            <span className="text-sm font-bold text-slate-900">
                              {item.taskNameTh}
                            </span>
                          </div>

                          {item.criteriaTh && (
                            <p className="text-xs text-slate-500">
                              เกณฑ์: {item.criteriaTh}
                            </p>
                          )}

                          {item.measuredValue && (
                            <p className="text-xs text-slate-700">
                              ค่าที่วัดได้: <strong className="font-mono font-bold text-blue-700">{item.measuredValue}</strong>
                              {item.minValue != null && ` (เกณฑ์มาตรฐาน >= ${item.minValue})`}
                            </p>
                          )}

                          {item.notes && (
                            <p className="text-xs text-rose-700 font-medium mt-1">
                              หมายเหตุช่าง: {item.notes}
                            </p>
                          )}
                        </div>

                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold shrink-0 ${
                            isItemNG
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>

                      {/* Photo Thumbnail if attached */}
                      {item.photoUrl && (
                        <div className="mt-3 pt-2.5 border-t border-slate-200/60 flex items-center gap-3">
                          <div
                            onClick={() =>
                              setLightboxPhoto({
                                url: item.photoUrl,
                                title: `ภาพหลักฐาน: ข้อ #${item.itemNo} ${item.taskNameTh}`,
                              })
                            }
                            className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-300 bg-slate-100 cursor-pointer shadow-xs shrink-0"
                          >
                            <img
                              src={item.photoUrl}
                              alt="Defect evidence thumbnail"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                              <ZoomIn className="w-5 h-5" />
                            </div>
                          </div>

                          <div className="space-y-1">
                            <span className="text-xs font-bold text-purple-700 flex items-center gap-1">
                              <Camera className="w-3.5 h-3.5" /> ภาพถ่ายหลักฐานความบกพร่อง
                            </span>
                            <p className="text-[11px] text-slate-500">
                              คลิกที่รูปภาพเพื่อดูภาพขยายใหญ่เต็มหน้าจอ
                            </p>
                            {item.photoUrl.startsWith('http') && (
                              <a
                                href={item.photoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 underline font-semibold"
                              >
                                <ExternalLink className="w-3 h-3" /> เปิดดูใน Google Drive
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Informative notice for legacy records without photo URL */}
                      {!item.photoUrl && item.hasPhoto && (
                        <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex items-center gap-2 text-xs text-amber-800 bg-amber-50/90 p-2.5 rounded-lg border border-amber-200">
                          <Camera className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>รายการนี้ระบุว่าเคยถ่ายรูปไว้ แต่บันทึกด้วยระบบเดิม (v1.0.0) ก่อนที่จะเปิดใช้งานระบบส่งไฟล์ขึ้น Google Drive</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Inspector Signature */}
            {selectedRecord.signatureDataUrl && (
              <div className="pt-2 border-t border-slate-200">
                <span className="text-xs font-bold text-slate-700 block mb-1.5">
                  ลายมือชื่อผู้ตรวจสอบ (Inspector Signature):
                </span>
                <div className="w-48 h-20 bg-slate-50 border border-slate-300 rounded-lg p-1 flex items-center justify-center">
                  <img
                    src={selectedRecord.signatureDataUrl}
                    alt="Inspector signature"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        </Modal>
      );
    })()}

      {/* Lightbox Full-screen Photo Modal */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="relative max-w-3xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900 truncate">
                {lightboxPhoto.title}
              </h3>
              <button
                type="button"
                onClick={() => setLightboxPhoto(null)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-auto flex items-center justify-center bg-slate-900">
              <img
                src={lightboxPhoto.url}
                alt={lightboxPhoto.title}
                className="max-h-[70vh] w-auto object-contain rounded-lg shadow-lg"
              />
            </div>

            <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
              <span>สามารถบันทึกหรือขยายดูรายละเอียดความเสียหายได้</span>
              {lightboxPhoto.url.startsWith('http') && (
                <a
                  href={lightboxPhoto.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline font-semibold flex items-center gap-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> เปิดรูปต้นฉบับในแท็บใหม่
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
